import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatNumber } from '../components/shared/UI';
import {
  Building2, Home, MapPin, Search, Plus, FileDown, Loader2, Printer,
  Users, UserCheck, ShieldCheck, Crown, Building, GraduationCap, Users2, ChevronRight, Info
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { wilayahTargetApi, partisipanApi, statsApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { KECAMATAN_LIST as ALL_KEC } from '../mock/mockData';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============ Kecamatan Page (dengan dedup) ============
export const Kecamatan = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    statsApi.kecamatanDetail().then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(k => k.kecamatan?.toLowerCase().includes(search.toLowerCase()));

  const totals = filtered.reduce((a, b) => ({
    pelopor: a.pelopor + (b.pelopor_count||0),
    nonPelopor: a.nonPelopor + (b.non_pelopor_count||0),
    saksi: a.saksi + (b.saksi||0),
    simpatisan: a.simpatisan + (b.simpatisan||0),
    total: a.total + (b.total_unik||0),
  }), { pelopor: 0, nonPelopor: 0, saksi: 0, simpatisan: 0, total: 0 });

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-bold text-orange-900">Anti-Duplikat Otomatis</p>
          <p className="text-xs text-orange-800 font-medium mt-1">
            Jumlah orang dihitung <b>unik</b> berdasarkan identitas tunggal / nama+kecamatan. Prioritas:
            <span className="font-bold"> Pelopor → Non Pelopor → Saksi → Relawan</span>.
            Bila seseorang terdaftar dengan lebih dari satu peran, ia dihitung sekali sesuai peran dengan prioritas tertinggi.
          </p>
        </div>
      </div>

      {/* Ringkasan total */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { l: 'Total Orang Unik', v: totals.total, icon: Users, c: 'orange' },
          { l: 'Pelopor', v: totals.pelopor, icon: UserCheck, c: 'amber' },
          { l: 'Non Pelopor', v: totals.nonPelopor, icon: UserCheck, c: 'amber' },
          { l: 'Saksi', v: totals.saksi, icon: ShieldCheck, c: 'emerald' },
          { l: 'Relawan (Murni)', v: totals.simpatisan, icon: Users, c: 'blue' },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-5 card-shadow card-hover">
            <div className={`w-11 h-11 rounded-xl bg-${s.c}-50 flex items-center justify-center mb-3`}>
              <s.icon className={`w-5 h-5 text-${s.c}-600`} />
            </div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{formatNumber(s.v)}</h3>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-extrabold">Rekap per Kecamatan</h3>
            <p className="text-sm text-gray-500 font-medium">Data unik dengan hirarki Pelopor &gt; Non Pelopor &gt; Saksi &gt; Relawan. Klik nama kecamatan atau angka mana pun untuk lihat data aslinya.</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari kecamatan..." className="pl-9 h-10 w-64" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">Kecamatan</TableHead>
                <TableHead className="font-extrabold text-center">Desa</TableHead>
                <TableHead className="font-extrabold text-center">RW</TableHead>
                <TableHead className="font-extrabold text-center">RT</TableHead>
                <TableHead className="font-extrabold text-center">TPS</TableHead>
                <TableHead className="font-extrabold text-right">Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Non Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Saksi</TableHead>
                <TableHead className="font-extrabold text-right">Relawan</TableHead>
                <TableHead className="font-extrabold text-right bg-orange-100">Total Unik</TableHead>
                <TableHead className="font-extrabold text-center">DPC</TableHead>
                <TableHead className="font-extrabold text-center">DPRa</TableHead>
                <TableHead className="font-extrabold text-center">Angg. Pelopor</TableHead>
                <TableHead className="font-extrabold text-center">RKI</TableHead>
                <TableHead className="font-extrabold text-right">Realisasi</TableHead>
                <TableHead className="font-extrabold">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={16} className="text-center py-8 text-gray-400 font-semibold">Memuat...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={16} className="text-center py-8 text-gray-400 font-semibold">Belum ada data</TableCell></TableRow>
              ) : filtered.map(k => {
                const pct = k.target ? Math.round(((k.realisasi||0)/k.target)*100) : 0;
                const kec = encodeURIComponent(k.kecamatan);
                return (
                  <TableRow key={k.kecamatan} className="hover:bg-orange-50/30">
                    <TableCell className="font-bold whitespace-nowrap">
                      <Building2 className="w-4 h-4 inline mr-2 text-orange-500" />
                      <Link to={`/desa?kec=${kec}`} className="hover:text-orange-600 hover:underline">{k.kecamatan}</Link>
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      <Link to={`/desa?kec=${kec}`} className="text-gray-600 hover:text-orange-600 hover:underline">{k.desa_count || 0}</Link>
                    </TableCell>
                    <TableCell className="text-center font-semibold">
                      <Link to={`/rw?kec=${kec}`} className="text-gray-600 hover:text-orange-600 hover:underline">{k.rw_count || 0}</Link>
                    </TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{k.rt_count || 0}</TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{k.tps_count || 0}</TableCell>
                    <TableCell className="text-right font-bold text-amber-600">
                      <Link to={`/partisipan?kecamatan=${kec}&peran=pelopor`} className="hover:underline">{formatNumber(k.pelopor_count||0)}</Link>
                    </TableCell>
                    <TableCell className="text-right font-bold text-orange-600">
                      <Link to={`/partisipan?kecamatan=${kec}&peran=non_pelopor`} className="hover:underline">{formatNumber(k.non_pelopor_count||0)}</Link>
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">
                      <Link to={`/partisipan?kecamatan=${kec}&peran=saksi`} className="hover:underline">{formatNumber(k.saksi||0)}</Link>
                    </TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      <Link to={`/partisipan?kecamatan=${kec}&peran=simpatisan`} className="hover:underline">{formatNumber(k.simpatisan||0)}</Link>
                    </TableCell>
                    <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">
                      <Link to={`/partisipan?kecamatan=${kec}`} className="hover:underline">{formatNumber(k.total_unik||0)}</Link>
                    </TableCell>
                    <TableCell className="text-center">{k.dpc > 0 ? <Link to={`/pengurus-dpc?kecamatan=${kec}`}><Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200">{k.dpc}</Badge></Link> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{k.dpra > 0 ? <Link to={`/pengurus-dpra?kecamatan=${kec}`}><Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">{k.dpra}</Badge></Link> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{k.pelopor > 0 ? <Link to={`/pelopor?kecamatan=${kec}`}><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">{k.pelopor}</Badge></Link> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{k.rki > 0 ? <Link to={`/rki?kecamatan=${kec}`}><Badge className="bg-red-100 text-red-700 hover:bg-red-200">{k.rki}</Badge></Link> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-right font-bold text-orange-600">{formatNumber(k.realisasi||0)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex-1 min-w-[60px]">
                          <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100,pct)}%`}}></div>
                        </div>
                        <span className="text-xs font-extrabold text-orange-600 w-8">{pct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// ============ DESA / KELURAHAN (list informatif) ============
export const Desa = () => {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKec, setFilterKec] = useState(searchParams.get('kec') || 'all');

  useEffect(() => { statsApi.desaDetail().then(r => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const kecList = [...new Set(items.map(i => i.kecamatan))].filter(Boolean).sort();
  const filtered = items.filter(d =>
    (filterKec === 'all' || d.kecamatan === filterKec) &&
    (d.desa?.toLowerCase().includes(search.toLowerCase()) || d.kecamatan?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-5 gap-4">
        {[
          { l: 'Total Desa/Kel Aktif', v: items.length },
          { l: 'Total Kecamatan', v: kecList.length },
          { l: 'Total Pelopor', v: items.reduce((a,b) => a + (b.pelopor_count||0), 0) },
          { l: 'Total Non Pelopor', v: items.reduce((a,b) => a + (b.non_pelopor_count||0), 0) },
          { l: 'Total Orang Unik', v: items.reduce((a,b) => a + (b.total||0), 0) },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-5 card-shadow card-hover">
            <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center mb-3"><Home className="w-5 h-5 text-orange-600" /></div>
            <p className="text-sm font-semibold text-gray-500">{s.l}</p>
            <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{formatNumber(s.v)}</h3>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h3 className="text-xl font-extrabold">Daftar Desa / Kelurahan</h3>
            <p className="text-sm text-gray-500 font-medium">Kolom RW = jumlah RW yang sudah tercover. Klik nama desa atau kolom RW untuk lihat RW-nya.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari desa..." className="pl-9 h-10 w-56" />
            </div>
            <Select value={filterKec} onValueChange={setFilterKec}>
              <SelectTrigger className="w-56 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kecamatan</SelectItem>
                {kecList.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">Desa / Kelurahan</TableHead>
                <TableHead className="font-extrabold">Kecamatan</TableHead>
                <TableHead className="font-extrabold text-center">RW</TableHead>
                <TableHead className="font-extrabold text-center">RT</TableHead>
                <TableHead className="font-extrabold text-center">TPS</TableHead>
                <TableHead className="font-extrabold text-right">Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Non Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Saksi</TableHead>
                <TableHead className="font-extrabold text-right">Relawan</TableHead>
                <TableHead className="font-extrabold text-right bg-orange-100">Total Unik</TableHead>
                <TableHead className="font-extrabold text-center">DPC</TableHead>
                <TableHead className="font-extrabold text-center">DPRa</TableHead>
                <TableHead className="font-extrabold text-center">Angg. Pelopor</TableHead>
                <TableHead className="font-extrabold text-center">RKI</TableHead>
                <TableHead className="font-extrabold text-right">Realisasi</TableHead>
                <TableHead className="font-extrabold">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={16} className="text-center py-8 text-gray-400 font-semibold">Memuat...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={16} className="text-center py-8 text-gray-400 font-semibold">Belum ada desa terisi. Tambahkan data di Partisipan atau upload DPT dulu.</TableCell></TableRow>
              ) : filtered.map(d => {
                const pct = d.target ? Math.round(((d.realisasi||d.total||0)/d.target)*100) : 0;
                return (
                  <TableRow key={`${d.kecamatan}-${d.desa}`} className="hover:bg-orange-50/30">
                    <TableCell className="font-bold">
                      <Home className="w-4 h-4 inline mr-2 text-orange-500" />
                      <Link to={`/rw?kec=${encodeURIComponent(d.kecamatan)}&desa=${encodeURIComponent(d.desa)}`} className="hover:text-orange-600 hover:underline">{d.desa}</Link>
                    </TableCell>
                    <TableCell className="font-semibold">{d.kecamatan}</TableCell>
                    <TableCell className="text-center">
                      <Link to={`/rw?kec=${encodeURIComponent(d.kecamatan)}&desa=${encodeURIComponent(d.desa)}`}>
                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 gap-1"><MapPin className="w-3 h-3" /> {d.rw_count}</Badge>
                      </Link>
                    </TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{d.rt_count || 0}</TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{d.tps_count || 0}</TableCell>
                    <TableCell className="text-right font-bold text-amber-600">{d.pelopor_count}</TableCell>
                    <TableCell className="text-right font-bold text-orange-600">{d.non_pelopor_count}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">{d.saksi}</TableCell>
                    <TableCell className="text-right font-medium text-blue-600">{d.simpatisan}</TableCell>
                    <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">{d.total_unik || d.total}</TableCell>
                    <TableCell className="text-center">{d.dpc > 0 ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{d.dpc}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{d.dpra > 0 ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{d.dpra}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{d.pelopor > 0 ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{d.pelopor}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{d.rki > 0 ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{d.rki}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-right font-bold text-orange-600">{formatNumber(d.realisasi !== undefined ? d.realisasi : d.total || 0)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex-1 min-w-[60px]">
                          <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100,pct)}%`}}></div>
                        </div>
                        <span className="text-xs font-extrabold text-orange-600 w-8">{pct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// ============ RW (list informatif) ============
export const RW = () => {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKec, setFilterKec] = useState(searchParams.get('kec') || 'all');
  const [filterDesa, setFilterDesa] = useState(searchParams.get('desa') || '');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { statsApi.rwDetail().then(r => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const TARGET_RW_KAB = 3228;
  const kecList = [...new Set(items.map(i => i.kecamatan))].filter(Boolean).sort();

  const filtered = items.filter(r => {
    const matchKec = filterKec === 'all' || r.kecamatan === filterKec;
    const matchDesa = !filterDesa || r.desa === filterDesa;
    const matchSearch =
      r.rw?.toLowerCase().includes(search.toLowerCase()) ||
      r.desa?.toLowerCase().includes(search.toLowerCase()) ||
      r.kecamatan?.toLowerCase().includes(search.toLowerCase());
    const status = r.total > 5 ? 'kuat' : r.total >= 2 ? 'sedang' : 'lemah';
    const matchStatus = filterStatus === 'all' || status === filterStatus;
    return matchKec && matchDesa && matchSearch && matchStatus;
  });

  const kuatCount = items.filter(i => (i.total || 0) > 5).length;
  const sedangCount = items.filter(i => (i.total || 0) >= 2 && (i.total || 0) <= 5).length;
  const lemahCount = items.filter(i => (i.total || 0) <= 1).length;
  const blankSpotCount = Math.max(0, TARGET_RW_KAB - items.length);

  return (
    <div className="space-y-6">
      {/* 5 KPI CARD MONITORING SISIR RW */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        {[
          { l: 'RW Tercover (SISIR)', v: items.length, sub: `dari ${TARGET_RW_KAB.toLocaleString()} target (${Math.round((items.length / TARGET_RW_KAB) * 100)}%)`, icon: MapPin, c: 'orange' },
          { l: 'RW Basis Kuat (>5)', v: kuatCount, sub: 'Struktur & relawan mapan', icon: ShieldCheck, c: 'emerald' },
          { l: 'RW Sedang (2–5)', v: sedangCount, sub: 'Perlu penguatan saksi', icon: Users, c: 'amber' },
          { l: 'RW Lemah (1 orang)', v: lemahCount, sub: 'Prioritas rekrutmen', icon: UserCheck, c: 'blue' },
          { l: 'Blank Spot RW', v: blankSpotCount, sub: 'Belum ada relawan terdaftar', icon: Building2, c: 'rose' },
        ].map(s => (
          <div key={s.l} className="bg-white rounded-2xl p-4 sm:p-5 card-shadow card-hover border border-gray-100">
            <div className={`w-10 h-10 rounded-xl bg-${s.c}-50 flex items-center justify-center mb-2.5`}>
              <s.icon className={`w-5 h-5 text-${s.c}-600`} />
            </div>
            <p className="text-xs font-bold text-gray-500">{s.l}</p>
            <h3 className="text-xl sm:text-2xl font-black text-gray-900 mt-0.5">{formatNumber(s.v)}</h3>
            {s.sub && <p className="text-[10px] text-gray-400 font-semibold mt-1 truncate">{s.sub}</p>}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow border border-gray-100">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h3 className="text-lg sm:text-xl font-black text-gray-900">Monitoring Penetrasi SISIR RW</h3>
            <p className="text-xs text-gray-500 font-medium">
              Target 3.228 RW Kabupaten Sukabumi · Status kekuatan relawan per RW
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* Filter Status Pills */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl text-xs font-bold">
              <button
                type="button"
                onClick={() => setFilterStatus('all')}
                className={`px-2.5 py-1 rounded-lg transition ${filterStatus === 'all' ? 'bg-white shadow-xs text-orange-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Semua ({items.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('kuat')}
                className={`px-2.5 py-1 rounded-lg transition ${filterStatus === 'kuat' ? 'bg-white shadow-xs text-emerald-700' : 'text-gray-600 hover:text-gray-900'}`}
              >
                🟢 Kuat ({kuatCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('sedang')}
                className={`px-2.5 py-1 rounded-lg transition ${filterStatus === 'sedang' ? 'bg-white shadow-xs text-amber-700' : 'text-gray-600 hover:text-gray-900'}`}
              >
                🟡 Sedang ({sedangCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('lemah')}
                className={`px-2.5 py-1 rounded-lg transition ${filterStatus === 'lemah' ? 'bg-white shadow-xs text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
              >
                🔵 Lemah ({lemahCount})
              </button>
            </div>

            {filterDesa && (
              <button onClick={() => setFilterDesa('')} className="flex items-center gap-1.5 h-9 px-3 rounded-xl bg-orange-100 text-orange-700 text-xs font-bold hover:bg-orange-200">
                Desa: {filterDesa} <span className="text-orange-900">✕</span>
              </button>
            )}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari RW / Desa..." className="pl-9 h-9 w-48 text-xs font-medium" />
            </div>
            <Select value={filterKec} onValueChange={setFilterKec}>
              <SelectTrigger className="w-48 h-9 text-xs font-medium"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kecamatan</SelectItem>
                {kecList.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-orange-50/50">
                <TableHead className="font-extrabold">RW</TableHead>
                <TableHead className="font-extrabold">Desa / Kel</TableHead>
                <TableHead className="font-extrabold">Kecamatan</TableHead>
                <TableHead className="font-extrabold text-center">RT</TableHead>
                <TableHead className="font-extrabold text-center">TPS</TableHead>
                <TableHead className="font-extrabold text-right">Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Non Pelopor</TableHead>
                <TableHead className="font-extrabold text-right">Saksi</TableHead>
                <TableHead className="font-extrabold text-right">Relawan</TableHead>
                <TableHead className="font-extrabold text-right bg-orange-100">Total Unik</TableHead>
                <TableHead className="font-extrabold text-center">DPC</TableHead>
                <TableHead className="font-extrabold text-center">DPRa</TableHead>
                <TableHead className="font-extrabold text-center">Angg. Pelopor</TableHead>
                <TableHead className="font-extrabold text-center">RKI</TableHead>
                <TableHead className="font-extrabold text-right">Realisasi</TableHead>
                <TableHead className="font-extrabold">Progress</TableHead>
                <TableHead className="font-extrabold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={17} className="text-center py-8 text-gray-400 font-semibold">Memuat...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={17} className="text-center py-8 text-gray-400 font-semibold">Belum ada RW tercover</TableCell></TableRow>
              ) : filtered.map(r => {
                const pct = r.target ? Math.round(((r.realisasi||r.total||0)/r.target)*100) : 0;
                return (
                  <TableRow key={`${r.kecamatan}-${r.desa}-${r.rw}`} className="hover:bg-orange-50/30">
                    <TableCell><span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-orange-100 text-orange-700 text-xs font-bold"><MapPin className="w-3 h-3" /> {r.rw}</span></TableCell>
                    <TableCell className="font-semibold">{r.desa || '-'}</TableCell>
                    <TableCell className="font-medium">{r.kecamatan}</TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{r.rt_count || 0}</TableCell>
                    <TableCell className="text-center font-semibold text-gray-600">{r.tps_count || 0}</TableCell>
                    <TableCell className="text-right font-bold text-amber-600">{r.pelopor_count}</TableCell>
                    <TableCell className="text-right font-bold text-orange-600">{r.non_pelopor_count}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-600">{r.saksi}</TableCell>
                    <TableCell className="text-right font-medium text-blue-600">{r.simpatisan}</TableCell>
                    <TableCell className="text-right font-extrabold text-orange-600 bg-orange-50/30">{r.total_unik || r.total}</TableCell>
                    <TableCell className="text-center">{r.dpc > 0 ? <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">{r.dpc}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{r.dpra > 0 ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{r.dpra}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{r.pelopor > 0 ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{r.pelopor}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-center">{r.rki > 0 ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{r.rki}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
                    <TableCell className="text-right font-bold text-orange-600">{formatNumber(r.realisasi !== undefined ? r.realisasi : r.total || 0)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex-1 min-w-[60px]">
                          <div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100,pct)}%`}}></div>
                        </div>
                        <span className="text-xs font-extrabold text-orange-600 w-8">{pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={r.total > 5 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : r.total >= 2 ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                        {r.total > 5 ? 'Kuat' : r.total >= 2 ? 'Sedang' : 'Lemah'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// ============ LAPORAN WILAYAH with PDF ============
export const LaporanWilayah = () => {
  const [wilayah, setWilayah] = useState([]);
  const [simpatisan, setSimpatisan] = useState([]);
  const [pelopor, setPelopor] = useState([]);
  const [nonPelopor, setNonPelopor] = useState([]);
  const [saksi, setSaksi] = useState([]);
  const [selectedKec, setSelectedKec] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    Promise.all([
      wilayahTargetApi.list(), partisipanApi.list(),
    ]).then(([w, p]) => {
      // Selalu tampilkan semua 47 kecamatan di dropdown/ringkasan, walau
      // belum ada Target Suara yang diisi untuk kecamatan itu.
      const rows = ALL_KEC.map(k => w.data.find(x => x.kecamatan === k.name) || { id: k.name, kecamatan: k.name, baseline: 0, target: 0, realisasi: 0 });
      setWilayah(rows);
      setSimpatisan(p.data.filter(x => x.peran === 'simpatisan' && !x.is_pelopor));
      setPelopor(p.data.filter(x => x.peran === 'pelopor' || x.is_pelopor));
      setNonPelopor(p.data.filter(x => x.peran === 'non_pelopor'));
      setSaksi(p.data.filter(x => x.peran === 'saksi'));
      if (rows.length > 0) setSelectedKec(rows[0].kecamatan);
    });
  }, []);

  const getKecData = (name) => {
    const w = wilayah.find(x => x.kecamatan === name) || { baseline: 0, target: 0, realisasi: 0 };
    const s = simpatisan.filter(x => x.kecamatan === name);
    const pl = pelopor.filter(x => x.kecamatan === name);
    const np = nonPelopor.filter(x => x.kecamatan === name);
    const sk = saksi.filter(x => x.kecamatan === name);
    return { w, s, pl, np, sk };
  };

  const generatePDF = async () => {
    if (!selectedKec) return;
    setGenerating(true);
    try {
      const { w, s, pl, np, sk } = getKecData(selectedKec);
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFillColor(249, 115, 22);
      doc.rect(0, 0, pageWidth, 90, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22); doc.setFont('helvetica', 'bold');
      doc.text('BP3', 40, 40);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text('BIDANG PEMENANGAN PEMILU DAN PILKADA PKS KAB. SUKABUMI', 40, 56);
      doc.setFontSize(9);
      doc.text('Laporan Pemenangan per Kecamatan', 40, 72);
      doc.text(`Cetak: ${new Date().toLocaleString('id-ID')}`, pageWidth - 40, 40, { align: 'right' });

      doc.setTextColor(31, 41, 55); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      doc.text(`Kecamatan ${selectedKec}`, 40, 130);
      doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(107, 114, 128);
      const pct = w.target ? Math.round((w.realisasi/w.target)*100) : 0;
      doc.text(`Progress Pemenangan: ${pct}% dari target`, 40, 148);

      const cardY = 170;
      const cards = [
        { l: 'Baseline', v: formatNumber(w.baseline) },
        { l: 'Target', v: formatNumber(w.target) },
        { l: 'Realisasi', v: formatNumber(w.realisasi) },
        { l: 'Relawan', v: s.length.toString() },
        { l: 'Pelopor', v: pl.length.toString() },
        { l: 'Non Pelopor', v: np.length.toString() },
        { l: 'Saksi TPS', v: sk.length.toString() },
      ];
      cards.forEach((c, i) => {
        const cw = (pageWidth - 80 - 30) / 7;
        const x = 40 + i * (cw + 5);
        doc.setFillColor(255, 247, 237);
        doc.roundedRect(x, cardY, cw, 60, 4, 4, 'F');
        doc.setTextColor(107, 114, 128); doc.setFontSize(7); doc.setFont('helvetica', 'bold');
        doc.text(c.l.toUpperCase(), x + 8, cardY + 16);
        doc.setTextColor(249, 115, 22); doc.setFontSize(13);
        doc.text(c.v, x + 8, cardY + 40);
      });

      doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text('Progress Target vs Realisasi', 40, 260);
      doc.setTextColor(249, 115, 22);
      doc.text(`${pct}%`, pageWidth - 40, 260, { align: 'right' });
      doc.setFillColor(243, 244, 246);
      doc.roundedRect(40, 270, pageWidth - 80, 16, 4, 4, 'F');
      doc.setFillColor(249, 115, 22);
      doc.roundedRect(40, 270, Math.max(16, (pageWidth - 80) * Math.min(1, pct/100)), 16, 4, 4, 'F');

      autoTable(doc, {
        startY: 305,
        head: [['Nama Relawan', 'HP', 'Desa/Kel', 'RW/RT', 'Status']],
        body: s.slice(0, 15).map(x => [x.nama, x.hp || '-', x.desa || '-', `${x.rw || '-'} / ${x.rt || '-'}`, x.status]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22], textColor: [255,255,255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [255, 247, 237] },
        margin: { left: 40, right: 40 },
      });

      const pelopourY = doc.lastAutoTable.finalY + 20;
      doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text(`Daftar Pelopor (${pl.length})`, 40, pelopourY);
      autoTable(doc, {
        startY: pelopourY + 5,
        head: [['Nama Pelopor', 'Jabatan', 'HP', 'Desa/Kel']],
        body: pl.slice(0, 15).map(x => [x.nama, x.jabatan, x.hp || '-', x.desa || '-']),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22], textColor: [255,255,255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [255, 247, 237] },
        margin: { left: 40, right: 40 },
      });

      const nonPelopourY = doc.lastAutoTable.finalY + 20;
      doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text(`Daftar Non Pelopor (${np.length})`, 40, nonPelopourY);
      autoTable(doc, {
        startY: nonPelopourY + 5,
        head: [['Nama Non Pelopor', 'Jabatan', 'HP', 'Desa/Kel']],
        body: np.slice(0, 15).map(x => [x.nama, x.jabatan, x.hp || '-', x.desa || '-']),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22], textColor: [255,255,255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [255, 247, 237] },
        margin: { left: 40, right: 40 },
      });

      const saksiY = doc.lastAutoTable.finalY + 20;
      doc.setTextColor(31, 41, 55); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text(`Daftar Saksi TPS (${sk.length})`, 40, saksiY);
      autoTable(doc, {
        startY: saksiY + 5,
        head: [['Nama Saksi', 'TPS', 'HP', 'Desa/Kel', 'Status']],
        body: sk.slice(0, 15).map(x => [x.nama, x.tps || '-', x.hp || '-', x.desa || '-', x.status || '-']),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22], textColor: [255,255,255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [255, 247, 237] },
        margin: { left: 40, right: 40 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(156, 163, 175);
        doc.text(`BP3 - Halaman ${i} dari ${pageCount}`, pageWidth - 40, doc.internal.pageSize.getHeight() - 20, { align: 'right' });
        doc.text('© 2025 BP3. Konfidensial - Internal Tim Pemenangan.', 40, doc.internal.pageSize.getHeight() - 20);
      }

      doc.save(`Laporan_${selectedKec}_${new Date().toISOString().slice(0,10)}.pdf`);
      toast({ title: 'Laporan PDF berhasil dibuat', description: `Laporan ${selectedKec} sedang diunduh` });
    } catch (e) {
      console.error(e);
      toast({ title: 'Gagal membuat PDF', description: e.message, variant: 'destructive' });
    } finally { setGenerating(false); }
  };

  const { w, s, pl, np, sk } = selectedKec ? getKecData(selectedKec) : { w:{baseline:0,target:0,realisasi:0}, s:[], pl:[], np:[], sk:[] };
  const pct = w.target ? Math.round((w.realisasi/w.target)*100) : 0;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-extrabold">Cetak Laporan Wilayah</h3>
            <p className="text-sm text-gray-500 font-medium">Pilih kecamatan, lalu klik Cetak PDF untuk laporan siap kirim ke Ketua Tim</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedKec} onValueChange={setSelectedKec}>
              <SelectTrigger className="w-64 h-11"><SelectValue placeholder="Pilih kecamatan" /></SelectTrigger>
              <SelectContent>
                {wilayah.map(w => <SelectItem key={w.id} value={w.kecamatan}>{w.kecamatan}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={generatePDF} disabled={generating || !selectedKec} className="h-11 bg-orange-500 hover:bg-orange-600 gap-2 font-bold">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Cetak PDF
            </Button>
          </div>
        </div>

        {selectedKec && (
          <div className="grid grid-cols-7 gap-3 mb-5">
            {[
              { l: 'Baseline', v: formatNumber(w.baseline) },
              { l: 'Target', v: formatNumber(w.target) },
              { l: 'Realisasi', v: formatNumber(w.realisasi), c: 'text-orange-600' },
              { l: 'Relawan', v: s.length },
              { l: 'Pelopor', v: pl.length },
              { l: 'Non Pelopor', v: np.length },
              { l: 'Saksi TPS', v: sk.length },
            ].map(x => (
              <div key={x.l} className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{x.l}</p>
                <p className={`text-xl font-extrabold mt-1 ${x.c || 'text-gray-900'}`}>{x.v}</p>
              </div>
            ))}
          </div>
        )}
        {selectedKec && (
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="font-bold">Progress {selectedKec}</span><span className="font-extrabold text-orange-600">{pct}%</span></div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-400 to-orange-500" style={{width: `${Math.min(100,pct)}%`}}></div></div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl p-6 card-shadow">
        <h3 className="text-xl font-extrabold mb-5">Ringkasan Semua Kecamatan</h3>
        <div className="space-y-3">
          {wilayah.map(kec => {
            const p = kec.target ? Math.round((kec.realisasi/kec.target)*100) : 0;
            return (
              <div key={kec.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-orange-300 card-hover">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold">{kec.kecamatan}</p>
                    <p className="text-xs text-gray-500 font-medium">Realisasi {formatNumber(kec.realisasi)} dari {formatNumber(kec.target)} ({p}%)</p>
                  </div>
                </div>
                <Button variant="outline" onClick={() => { setSelectedKec(kec.kecamatan); setTimeout(generatePDF, 200); }} className="font-bold gap-2">
                  <Printer className="w-4 h-4" /> Cetak
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
